/**
 * Agent Progress 视图模型适配层
 *
 * 将既有 swagger DTO（IApiInquiryStats / IApiCompanyDto / IApiInquiryInfoDTO / IApiInquiryItemDto）
 * 适配为 Figma 重构所需的 VM 结构。组件层只接收 VM，不直接依赖 swagger 类型。
 *
 * 设计原则（参见 specs/001-agent-progress-figma-rebuild/data-model.md）：
 *  1. 纯函数 + 无副作用
 *  2. 缺字段一律返回 '—'（U+2014），禁止透传 undefined
 *  3. 不引入新的网络字段；后端 schema 演进时只改本文件
 */

import dayjs from '@/utils/dayjs';

import type {
  AgentInquiryStats,
  AgentProgressHeaderVM,
  AgentProgressViewModel,
  ClarificationBoardVM,
  ContactInReplyVM,
  IApiCompanyDto,
  IApiInquiryContactResp,
  InquiryDetailsVM,
  InquiryStatus,
  SupplierRowVM,
  SupplierStatus,
  SuppliersTableVM,
} from '../types';
import type { IApiInquiryInfoDTO, IApiInquiryItemDto } from '@/services/swagger/easourcing/models';

// ---------------------------------------------------------------------------
// 常量
// ---------------------------------------------------------------------------

/** 缺字段统一占位符（U+2014 EM DASH） */
export const EMPTY_PLACEHOLDER = '—';

const STATUS_LABEL_MAP: Record<InquiryStatus, string> = {
  running: 'In progress',
  completed: 'Completed',
  paused: 'Paused',
};

const SUPPLIER_STATUS_LABEL_MAP: Record<SupplierStatus, string> = {
  'quote-ready': 'Quote Ready',
  'clarify-needed': 'Clarify Needed',
  'awaiting-reply': 'Awaiting Reply',
  'ai-thinking': 'AI thinking',
  completed: 'Completed',
};

// ---------------------------------------------------------------------------
// 基础 mapper
// ---------------------------------------------------------------------------

/**
 * 将后端 stats.status 数值映射到 VM 状态枚举。
 * 后端约定：`0-running, 1-paused, 2-completed`（来源：IApiInquiryStats 字段注释）。
 * 任何越界值兜底为 'running'，避免 UI 因脏数据崩溃。
 */
export function mapInquiryStatus(status: number | undefined): InquiryStatus {
  switch (status) {
    case 0:
      return 'running';
    case 1:
      return 'paused';
    case 2:
      return 'completed';
    default:
      return 'running';
  }
}

/** 状态枚举 → 显示文案 */
export function mapStatusLabel(status: InquiryStatus): string {
  return STATUS_LABEL_MAP[status];
}

/**
 * 供应商行状态映射（基于新接口 IApiInquiryContactResp.inquiryContactStatus）。
 *
 * 后端状态枚举：
 *  - AI_THINKING        → ai-thinking
 *  - AWAITING_RESPONSE  → awaiting-reply
 *  - PENDING_CLARIFICATION → clarify-needed
 *  - COMPLETED          → quote-ready
 *
 * @param statusStr - IApiInquiryContactResp.inquiryContactStatus
 */
export function mapSupplierStatusFromContact(statusStr: string | undefined): SupplierStatus {
  switch (statusStr) {
    case 'COMPLETED':
    case 'QUOTE_READY':
      return 'quote-ready';
    case 'PENDING_CLARIFICATION':
    case 'CLARIFY_NEEDED':
      return 'clarify-needed';
    case 'AWAITING_RESPONSE':
      return 'awaiting-reply';
    case 'AI_THINKING':
      return 'ai-thinking';
    default:
      return 'awaiting-reply';
  }
}

/**
 * 供应商行状态映射（兼容旧 IApiCompanyDto 数据源）。
 * @deprecated 优先使用 mapSupplierStatusFromContact
 */
export function mapSupplierStatus(raw: IApiCompanyDto | undefined): SupplierStatus {
  if (!raw) return 'awaiting-reply';
  if (raw.reachStatus === 0) {
    return raw.status === 3 ? 'quote-ready' : 'clarify-needed';
  }
  if (raw.reachStatus === 1) return 'awaiting-reply';
  if (raw.reachStatus === 2) return 'ai-thinking';
  return 'awaiting-reply';
}

/** 供应商状态 → 显示文案 */
export function mapSupplierStatusLabel(status: SupplierStatus): string {
  return SUPPLIER_STATUS_LABEL_MAP[status];
}

/**
 * 价格格式化。空值返回 EMPTY_PLACEHOLDER。
 *
 * @example formatPrice(45, 'USD') → 'USD 45'
 * @example formatPrice('45.5')    → '$ 45.5'
 * @example formatPrice(undefined) → '—'
 */
export function formatPrice(value: string | number | undefined | null, currency?: string): string {
  if (value === undefined || value === null || value === '') return EMPTY_PLACEHOLDER;
  const symbol = currency && currency.trim().length > 0 ? `${currency} ` : ' ';
  return `${symbol}${value}`;
}

// ---------------------------------------------------------------------------
// VM 构造器
// ---------------------------------------------------------------------------

/**
 * 构造 Header VM。
 *
 * @param stats     - 既有 useAgentInquiry().stats
 * @param progress  - 既有 useInqiryProgress(stats) 返回的 0~100
 */
export function buildHeaderVM(stats: AgentInquiryStats | null | undefined, progress: number): AgentProgressHeaderVM {
  const status = mapInquiryStatus(stats?.status);
  const title = stats?.inquiryName?.trim() ? stats.inquiryName : 'Untitled Inquiry';
  const dateCreatedDisplay = stats?.createTimestamp
    ? `Date Created: ${dayjs(stats.createTimestamp).format('MMM D, YYYY')}`
    : `Date Created: ${EMPTY_PLACEHOLDER}`;

  return {
    title,
    status,
    statusLabel: mapStatusLabel(status),
    progressPct: clampPct(progress),
    dateCreatedDisplay,
    canRename: status !== 'completed',
    canPauseResume: status !== 'completed',
  };
}

/**
 * 构造 Inquiry Details VM。
 *
 * 数据源：inquiryDetailsGet 返回的 inquiryInfo（买家/地区）+ item（产品/价格/数量/贸易条款）。
 * 字段不齐时按 EMPTY_PLACEHOLDER 兜底。
 *
 * @param inquiryInfo       - IApiInquiryDetailResp.inquiryInfo，可能为 null
 * @param item              - IApiInquiryDetailResp.item，可能为 null
 * @param fallbackBuyerName - 当 inquiryInfo.userName 缺失时的兜底（如登录用户名）
 */
export function buildInquiryDetailsVM(
  inquiryInfo: IApiInquiryInfoDTO | null | undefined,
  item: IApiInquiryItemDto | null | undefined,
  fallbackBuyerName?: string
): InquiryDetailsVM {
  const productName = nonEmpty(item?.productName);
  const unitPrice = formatPrice(item?.price, item?.currency);
  // 后端 quantity 是字符串（已含单位或纯数值），不强制拼 'units'，避免重复
  const quantity = nonEmpty(item?.quantity);
  const tradeTerms = nonEmpty(item?.tradeTerms);
  const buyerName =
    nonEmpty(inquiryInfo?.userName) !== EMPTY_PLACEHOLDER ? inquiryInfo!.userName : nonEmpty(fallbackBuyerName);
  const location = nonEmpty(inquiryInfo?.location);

  const fields: InquiryDetailsVM['fields'] = [
    { label: 'Product Name', value: productName },
    { label: 'Unit Price', value: unitPrice },
    { label: 'Quantity', value: quantity },
    { label: 'Trade terms', value: tradeTerms },
    { label: 'Buyer Name', value: buyerName },
    { label: 'Location', value: location },
  ];

  return { fields };
}

/**
 * 构造 Clarification Board VM。
 *
 * 当前阶段后端尚未返回 clarification 列表（无对应 swagger 类型），
 * 因此本期返回 null（隐藏整卡片），符合 data-model §4 渲染规则与 spec Edge Case 1。
 * 待后端字段就绪后，仅需在本函数内补充实际派生即可，组件 props 不变。
 *
 * @param clarifications - 预留入参；当前传入恒为 undefined / []
 */
export function buildClarificationBoardVM(
  clarifications?: Array<{
    id: string;
    question: string;
    replied: boolean;
    contacts: ContactInReplyVM[];
  }>
): ClarificationBoardVM | null {
  if (!clarifications || clarifications.length === 0) return null;

  const items = clarifications
    .filter(c => c.contacts.length > 0)
    .map(c => ({
      id: c.id,
      question: c.question,
      supplierCount: c.contacts.length,
      contacts: c.contacts,
    }));

  if (items.length === 0) return null;

  const totalCount = clarifications.length;
  const repliedCount = clarifications.filter(c => c.replied).length;

  return {
    repliedCount,
    totalCount,
    repliedBadgeText: `Replied ${repliedCount}/${totalCount}`,
    items,
  };
}

/**
 * 构造 Suppliers Table VM（基于新接口 IApiInquiryContactResp）。
 *
 * @param contacts  - useInquiryContacts().contacts（inquiry-contact/list 接口返回）
 * @param page      - 1-based 当前页
 * @param pageSize  - 每页条数
 * @param total     - 总数（来自接口）
 */
export function buildSuppliersTableVM(
  contacts: IApiInquiryContactResp[] | undefined,
  page: number,
  pageSize: number,
  total: number
): SuppliersTableVM {
  const rows: SupplierRowVM[] = (contacts ?? []).map(c => {
    const status = mapSupplierStatusFromContact(c.inquiryContactStatus);
    return {
      id: c.inquiryContactId ?? c.contactId ?? '',
      companyId: c.companyId ?? '',
      contactId: c.contactId ?? '',
      triggerMsgId: c.triggerMsgId ?? '',
      companyName: nonEmpty(c.companyName),
      contactName: nonEmpty(c.name),
      email: nonEmpty(c.email),
      status,
      statusLabel: mapSupplierStatusLabel(status),
      // Details 列：clarify-needed 时展示后端返回的待澄清问题；其他状态为空
      detailsText: status === 'clarify-needed' ? nonEmpty(c.details) : '',
      unitPrice: nonEmpty(c.quoteUnitPrice) !== EMPTY_PLACEHOLDER ? formatPrice(c.quoteUnitPrice) : EMPTY_PLACEHOLDER,
      totalQuotation: nonEmpty(c.quoteTotal) !== EMPTY_PLACEHOLDER ? formatPrice(c.quoteTotal) : EMPTY_PLACEHOLDER,
      delivery: nonEmpty(c.quoteDeliveryDate),
      payment: nonEmpty(c.quotePaymentMethod),
      additionalInfo: nonEmpty(c.additionalInfo),
      aiReplyEnabled: c.autoReplyEnabled === 1,
    };
  });

  return {
    rows,
    page,
    pageSize,
    total,
  };
}

/**
 * 顶层聚合：把 4 个 VM 聚合为 AgentProgressViewModel。
 * 返回 null 表示 stats 尚未就绪（仍处 loading），父层应继续展示骨架/loader。
 */
export function buildAgentProgressViewModel(input: {
  stats: AgentInquiryStats | null | undefined;
  inquiryInfo: IApiInquiryInfoDTO | null | undefined;
  item: IApiInquiryItemDto | null | undefined;
  contacts: IApiInquiryContactResp[] | undefined;
  /** @deprecated 兼容旧引用 */
  companies?: IApiCompanyDto[] | undefined;
  page: number;
  pageSize: number;
  total: number;
  progress: number;
  fallbackBuyerName?: string;
}): AgentProgressViewModel | null {
  const { stats, inquiryInfo, item, contacts, page, pageSize, total, progress, fallbackBuyerName } = input;
  if (!stats) return null;

  return {
    header: buildHeaderVM(stats, progress),
    inquiryDetails: buildInquiryDetailsVM(inquiryInfo, item, fallbackBuyerName),
    clarificationBoard: buildClarificationBoardVM(undefined),
    suppliersTable: buildSuppliersTableVM(contacts, page, pageSize, total),
  };
}

// ---------------------------------------------------------------------------
// 内部工具
// ---------------------------------------------------------------------------

function nonEmpty(value: string | undefined | null): string {
  if (value === undefined || value === null) return EMPTY_PLACEHOLDER;
  const trimmed = String(value).trim();
  return trimmed.length === 0 ? EMPTY_PLACEHOLDER : trimmed;
}

function clampPct(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n);
}
